import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar,
  Search,
  Sparkles,
  Filter,
  CheckCircle2,
  Flame,
  AlertTriangle,
} from "lucide-react";
import {
  LEAD_PLATFORMS,
  campaignPlatforms,
} from "../../services/collavueStore";

const STAGES = [
  {
    key: "queries",
    label: "Building search queries",
    icon: Sparkles,
    detail: "Combining keywords with buying-intent phrases",
  },
  {
    key: "scan",
    label: "Scanning your sources",
    icon: Radar,
    detail: "Pulling fresh posts that match your keywords",
  },
  {
    key: "filter",
    label: "Filtering noise",
    icon: Filter,
    detail: "Removing spam, jobs, and off-topic chatter",
  },
  {
    key: "score",
    label: "Scoring intent",
    icon: Flame,
    detail: "Ranking buying intent and pain-point signals",
  },
  {
    key: "save",
    label: "Saving leads",
    icon: CheckCircle2,
    detail: "Writing scored leads to your inbox",
  },
];

function useRotating(items, intervalMs, isActive) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!isActive || !items.length) return undefined;
    const id = setInterval(() => {
      setI((prev) => (prev + 1) % items.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [isActive, items.length, intervalMs]);
  return items.length ? items[i % items.length] : null;
}

/**
 * Full-screen animated overlay shown while a campaign sync is running.
 *
 * Props:
 *   open       boolean - whether the overlay is visible
 *   campaign   { name, primaryKeywords } - context to display
 *   result     { stats: {...} } | null - present when sync completes
 *   error      string | null
 *   onClose    () => void - called when the user dismisses the final state
 */
export default function SyncAnimationOverlay({
  open,
  campaign,
  result,
  error,
  onClose,
}) {
  const [stageIdx, setStageIdx] = useState(0);
  const [postCount, setPostCount] = useState(0);
  const startedAt = useRef(0);

  const keywords = useMemo(
    () =>
      (campaign?.primaryKeywords || [])
        .map((k) => String(k || "").trim())
        .filter(Boolean)
        .slice(0, 8),
    [campaign]
  );

  // The sources this sync is actually searching, so the overlay reflects the
  // campaign's selection instead of naming platforms it isn't touching. Kept
  // short (X · LinkedIn · Reddit) to fit the stat tile.
  const platformSummary = useMemo(
    () =>
      campaignPlatforms(campaign)
        .map((key) => LEAD_PLATFORMS.find((p) => p.v === key)?.short || key)
        .join(" · "),
    [campaign]
  );

  const isRunning = open && !result && !error;
  const currentKeyword = useRotating(keywords, 1100, isRunning);

  useEffect(() => {
    if (!isRunning) return undefined;
    setStageIdx(0);
    setPostCount(0);
    startedAt.current = Date.now();
    const stageId = setInterval(() => {
      setStageIdx((s) => Math.min(STAGES.length - 1, s + 1));
    }, 2200);
    const counterId = setInterval(() => {
      setPostCount((c) => c + Math.max(1, Math.floor(Math.random() * 4)));
    }, 250);
    return () => {
      clearInterval(stageId);
      clearInterval(counterId);
    };
  }, [isRunning]);

  useEffect(() => {
    if (result) setStageIdx(STAGES.length - 1);
  }, [result]);

  if (!open) return null;

  const stats = result?.stats || {};
  const showResult = !!result;
  const showError = !!error;

  return (
    <AnimatePresence>
      <motion.div
        key="sync-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4"
        role="dialog"
        aria-modal="true"
      >
        <motion.div
          initial={{ y: 24, scale: 0.94, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 12, scale: 0.98, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/5"
        >
          <Header
            campaign={campaign}
            showResult={showResult}
            showError={showError}
          />

          <div className="px-6 pb-6">
            {!showResult && !showError && (
              <RunningView
                stageIdx={stageIdx}
                currentKeyword={currentKeyword}
                postCount={postCount}
                keywords={keywords}
                platforms={platformSummary}
              />
            )}
            {showResult && (
              <ResultView stats={stats} onClose={onClose} />
            )}
            {showError && <ErrorView error={error} onClose={onClose} />}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Header({ campaign, showResult, showError }) {
  const accent = showError
    ? "from-rose-600 via-rose-500 to-orange-500"
    : showResult
      ? "from-emerald-500 via-teal-500 to-cyan-500"
      : "from-indigo-600 via-violet-600 to-fuchsia-600";

  return (
    <div
      className={`relative px-6 pt-7 pb-12 bg-gradient-to-br ${accent} text-white overflow-hidden`}
    >
      <ScannerVisual
        active={!showResult && !showError}
        showResult={showResult}
        showError={showError}
      />

      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/30">
          {showResult ? (
            <CheckCircle2 className="w-6 h-6" />
          ) : showError ? (
            <AlertTriangle className="w-6 h-6" />
          ) : (
            <Radar className="w-6 h-6" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/85 font-semibold">
            {showResult
              ? "Sync complete"
              : showError
                ? "Sync issue"
                : "Lead sync running"}
          </div>
          <div className="text-lg font-semibold truncate">
            {campaign?.name || "Campaign"}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScannerVisual({ active, showResult, showError }) {
  // Layered radar/scanner with sweeping arc and orbiting dots.
  const orbitDots = [0, 1, 2, 3, 4, 5];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -right-24 -top-24 w-80 h-80 opacity-90">
        {/* Pulsing rings */}
        <motion.div
          className="absolute inset-0 rounded-full border border-white/30"
          animate={
            active
              ? { scale: [0.5, 1.4], opacity: [0.55, 0] }
              : { scale: 1, opacity: 0.25 }
          }
          transition={{
            duration: 2.6,
            repeat: active ? Infinity : 0,
            ease: "easeOut",
          }}
        />
        <motion.div
          className="absolute inset-6 rounded-full border border-white/35"
          animate={
            active
              ? { scale: [0.55, 1.5], opacity: [0.55, 0] }
              : { scale: 1, opacity: 0.2 }
          }
          transition={{
            duration: 2.6,
            repeat: active ? Infinity : 0,
            ease: "easeOut",
            delay: 0.65,
          }}
        />
        <motion.div
          className="absolute inset-14 rounded-full bg-white/10"
          animate={
            active ? { opacity: [0.35, 0.1, 0.35] } : { opacity: 0.18 }
          }
          transition={{ duration: 1.8, repeat: active ? Infinity : 0 }}
        />

        {/* Radial sweep */}
        {active && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(255,255,255,0.45) 0deg, rgba(255,255,255,0) 90deg, rgba(255,255,255,0) 360deg)",
              maskImage:
                "radial-gradient(circle at 50% 50%, black 60%, transparent 62%)",
              WebkitMaskImage:
                "radial-gradient(circle at 50% 50%, black 60%, transparent 62%)",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
          />
        )}

        {/* Orbiting blips */}
        {active &&
          orbitDots.map((i) => (
            <motion.div
              key={i}
              className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-white/85 shadow-[0_0_10px_rgba(255,255,255,0.9)]"
              style={{ x: -3, y: -3 }}
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 3.2 + i * 0.4,
                repeat: Infinity,
                ease: "linear",
                delay: i * 0.18,
              }}
            >
              <div
                style={{
                  transform: `translate(${60 + (i % 3) * 18}px, 0)`,
                }}
              />
            </motion.div>
          ))}

        {/* Confetti spark when done */}
        {showResult && (
          <>
            {[...Array(10)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-white"
                initial={{ opacity: 1, scale: 0.6 }}
                animate={{
                  x: Math.cos((i / 10) * Math.PI * 2) * 110,
                  y: Math.sin((i / 10) * Math.PI * 2) * 110,
                  opacity: 0,
                  scale: 1.2,
                }}
                transition={{ duration: 1.1, delay: 0.05 * i }}
              />
            ))}
          </>
        )}

        {showError && (
          <motion.div
            className="absolute inset-12 rounded-full border-2 border-white/40"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
        )}
      </div>
    </div>
  );
}

function RunningView({ stageIdx, currentKeyword, postCount, keywords, platforms }) {
  return (
    <div className="-mt-8 relative">
      {/* Floating live keyword chips */}
      <div className="relative h-8 mb-4">
        <AnimatePresence mode="popLayout">
          {keywords.length > 0 && (
            <motion.div
              key={currentKeyword || "kw"}
              initial={{ y: 12, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -12, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-md ring-1 ring-slate-900/5 text-xs font-semibold text-slate-700"
            >
              <Search className="w-3.5 h-3.5 text-indigo-500" />
              <span className="truncate max-w-[260px]">
                Searching “{currentKeyword || "…"}”
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        <span>Live progress</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          Working
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <Stat
          label="Sources"
          value={platforms || "…"}
          icon={<Radar className="w-3.5 h-3.5 text-indigo-600" />}
        />
        <Stat
          label="Keyword"
          value={currentKeyword || "…"}
          icon={<Search className="w-3.5 h-3.5 text-indigo-600" />}
          animateKey={currentKeyword}
        />
        <Stat
          label="Posts seen"
          value={
            <motion.span
              key={postCount}
              initial={{ y: -4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.18 }}
              className="inline-block"
            >
              {postCount}
            </motion.span>
          }
          icon={<Sparkles className="w-3.5 h-3.5 text-indigo-600" />}
        />
      </div>

      <ol className="mt-5 space-y-2">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const status =
            i < stageIdx ? "done" : i === stageIdx ? "active" : "pending";
          return (
            <motion.li
              key={s.key}
              layout
              initial={false}
              animate={{
                backgroundColor:
                  status === "active"
                    ? "rgba(238, 242, 255, 0.7)"
                    : status === "done"
                      ? "rgba(236, 253, 245, 0.55)"
                      : "rgba(255,255,255,1)",
              }}
              transition={{ duration: 0.4 }}
              className={`relative overflow-hidden flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                status === "active"
                  ? "border-indigo-200"
                  : status === "done"
                    ? "border-emerald-100"
                    : "border-slate-200"
              }`}
            >
              {/* Animated shimmer bar across active row */}
              {status === "active" && (
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-indigo-100/80 to-transparent"
                  animate={{ x: ["0%", "400%"] }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              )}

              <div
                className={`relative mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                  status === "active"
                    ? "bg-indigo-600 text-white shadow-[0_4px_14px_-2px_rgba(79,70,229,0.6)]"
                    : status === "done"
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-100 text-slate-500"
                }`}
              >
                {status === "active" ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="inline-flex"
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </motion.span>
                ) : status === "done" ? (
                  <motion.span
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </motion.span>
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
              </div>
              <div className="min-w-0 flex-1 relative">
                <div className="text-sm font-semibold text-slate-900">
                  {s.label}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {s.detail}
                </div>
              </div>
              {status === "active" && (
                <div className="self-center h-1.5 bg-indigo-100 rounded-full overflow-hidden w-20">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{
                      duration: 1.4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ width: "60%" }}
                  />
                </div>
              )}
            </motion.li>
          );
        })}
      </ol>

      <p className="mt-5 text-center text-[11px] text-slate-500">
        This usually takes 10–30 seconds. You can leave this page open.
      </p>
    </div>
  );
}

function Stat({ label, value, icon, animateKey }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 overflow-hidden">
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-500">
        {icon}
        {label}
      </div>
      <AnimatePresence mode="popLayout">
        <motion.div
          key={String(animateKey ?? value)}
          initial={{ y: 6, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -6, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="text-sm font-semibold text-slate-900 truncate mt-0.5"
        >
          {value ?? "—"}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ResultView({ stats, onClose }) {
  const created = stats.leadsCreated || 0;
  const hot = stats.hotLeads || 0;
  const fetched = stats.postsFetched || 0;
  const noResults = created === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="-mt-6 relative"
    >
      <div className="grid grid-cols-3 gap-3">
        <ResultStat
          label="New leads"
          value={created}
          accent="text-blue-600"
          delay={0}
        />
        <ResultStat label="Hot" value={hot} accent="text-rose-600" delay={0.08} />
        <ResultStat label="Posts scanned" value={fetched} delay={0.16} />
      </div>

      {noResults ? (
        <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          No qualifying conversations matched your filters this window. Try a
          longer date range or broader keywords.
        </div>
      ) : (
        <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {created} new lead{created === 1 ? "" : "s"} added to your inbox
          {hot > 0 ? `, including ${hot} hot lead${hot === 1 ? "" : "s"}.` : "."}
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-sm border border-gray-300 text-sm font-semibold hover:bg-gray-50"
        >
          {noResults ? "Close" : "Go to inbox"}
        </button>
      </div>
    </motion.div>
  );
}

function ResultStat({ label, value, accent, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay, type: "spring", stiffness: 220 }}
      className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-center"
    >
      <div className={`text-2xl font-bold ${accent || "text-slate-900"}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">
        {label}
      </div>
    </motion.div>
  );
}

function ErrorView({ error, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="-mt-6 relative"
    >
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
        {error || "Something went wrong while syncing."}
      </div>
      <div className="mt-5 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50"
        >
          Close
        </button>
      </div>
    </motion.div>
  );
}
